import { ApolloClient, InMemoryCache, ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import introspection from './gql/introspection';
import runtime from './runtime';
import prepareDataForGraphQL from './utils/prepareDataForGraphQL';
import { getToken, onTokenUpdate, triggerUnauthenticatedError, logoutReasonKey } from './utils/session';
import wsClient from './wsClient';

// extract the host and protocol from the window location
const { host, protocol } = window.location;

// create the HTTP link for standard GraphQL queries/mutations
const httpLink = new UploadHttpLink({ uri: `${protocol}//${host}/api/graphql`, credentials: 'include' });

const wsLink = new GraphQLWsLink(wsClient);

// link to add the JWT token in authorization header
const addAuthorizationHeaderLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }) => {
        const token = getToken();

        if (!token) {
            return { headers };
        }

        return {
            headers: {
                ...headers,
                Authorization: `Bearer ${token}`,
                'Apollo-Require-Preflight': 'true',
            },
        };
    });

    return forward(operation);
});

// create a split link to either use HTTP or WS links based on the definition
const splitLink = ApolloLink.split(
    ({ query }) => {
        const definition = getMainDefinition(query);

        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([addAuthorizationHeaderLink, httpLink])
);

// link to prepare GraphQL variables for submissions
const prepareDataLink = new ApolloLink((operation, forward) => {
    operation.variables = prepareDataForGraphQL(operation.variables);

    return forward(operation);
});

// handle unauthenticated errors
const handleUnauthenticatedErrorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
        const isUnauthenticated = error.errors.some(({ extensions }) => extensions?.code === 'UNAUTHENTICATED');

        if (isUnauthenticated) {
            // if a token was present the server rejected it (expired / revoked) —
            // store a logout reason so the login page can explain what happened
            if (getToken()) {
                sessionStorage.setItem(logoutReasonKey, 'inactivity');
            }
            triggerUnauthenticatedError();
        }
    } else if (error) {
        console.error(error);
    }
});

const cache = new InMemoryCache({ possibleTypes: introspection.possibleTypes });

// initialize the apollo client
const client = new ApolloClient({
    link: ApolloLink.from([prepareDataLink, handleUnauthenticatedErrorLink, splitLink]),
    cache,
    devtools: runtime.apolloDevToolsEnabled
        ? {
              enabled: true,
              name: host,
          }
        : undefined,
});

onTokenUpdate(token => {
    if (!token) {
        // once the client is signed out make sure we dropped everything
        // ensure nothing sensitive may remain in the cache
        void client.clearStore();
    }
});

export default client;
